import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  SystemWorkspaceDefinition,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

import {
  listCallsSimulatorScenarios,
  runCallsControlledSimulation,
  type CallsSimulatorResult,
  type CallsSimulatorScenarioId,
} from '@/governor/calls-simulator/CallsControlledSimulator';

import {
  getGovernorLiveActions,
  subscribeGovernorLiveActions,
  type GovernorLiveAction,
} from '@/lib/governorEventBus';

import {
  projectCallsWorkspaceLiveEvidence,
} from '@/governor/calls-workspace/CallsWorkspaceLiveEvidenceProjection';

import CallsForegroundCertificationWorkspace from '@/components/governor/CallsForegroundCertificationWorkspace';

type Props = {
  workspace:
    SystemWorkspaceDefinition;
};

function resultClass(
  status:
    CallsSimulatorResult['steps'][number]['status'],
): string {
  if (status === 'pass') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  }

  if (status === 'fail') {
    return 'border-rose-200 bg-rose-50 text-rose-900';
  }

  return 'border-amber-200 bg-amber-50 text-amber-900';
}

export default function CallsOperationsWorkspace({
  workspace,
}: Props) {
  const scenarios =
    useMemo(
      () =>
        listCallsSimulatorScenarios(),
      [],
    );

  const [
    selectedScenario,
    setSelectedScenario,
  ] =
    useState<CallsSimulatorScenarioId>(
      'successful-call',
    );

  const [
    result,
    setResult,
  ] =
    useState<CallsSimulatorResult | null>(
      null,
    );

  const [
    liveActions,
    setLiveActions,
  ] =
    useState<readonly GovernorLiveAction[]>(
      () =>
        getGovernorLiveActions(),
    );

  useEffect(
    () =>
      subscribeGovernorLiveActions(
        actions =>
          setLiveActions(actions),
      ),
    [],
  );

  const liveEvidence =
    useMemo(
      () =>
        projectCallsWorkspaceLiveEvidence(
          liveActions,
        ),
      [liveActions],
    );

  return (
    <div className="mt-5 space-y-5">
      <CallsForegroundCertificationWorkspace />

      <section className="rounded-2xl border border-violet-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700">
              Live runtime evidence
            </p>

            <h4 className="mt-2 text-2xl font-bold text-slate-950">
              Calls Live Lifecycle
            </h4>

            <p className="mt-2 max-w-3xl text-sm text-slate-700">
              Reads the existing persistent Governor Calls event
              stream. This panel creates no second evidence store and
              never converts SIMULATED evidence into LIVE proof.
            </p>
          </div>

          <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
            <p>
              Mode: <b>{liveEvidence.mode}</b>
            </p>

            <p>
              Records:{' '}
              <b>{liveEvidence.totalCount}</b>
            </p>

            <p>
              Blockers:{' '}
              <b>{liveEvidence.blockerCount}</b>
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {[
            ['Success', liveEvidence.successCount],
            ['Failure', liveEvidence.failureCount],
            ['Timeout', liveEvidence.timeoutCount],
            ['Waiting', liveEvidence.waitingCount],
            ['Total', liveEvidence.totalCount],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3"
            >
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                {label}
              </p>

              <p className="mt-1 text-2xl font-bold text-slate-950">
                {value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-bold">
            Certification remains withheld
          </p>

          <p className="mt-1">
            {liveEvidence.certificationReason}
          </p>
        </div>

        {liveEvidence.records.length > 0 ? (
          <div className="mt-5 space-y-3">
            {liveEvidence.records
              .slice(0, 12)
              .map(record => (
                <div
                  key={record.id}
                  className={
                    'rounded-xl border p-4 ' +
                    (record.blocking
                      ? 'border-rose-200 bg-rose-50'
                      : 'border-emerald-200 bg-emerald-50')
                  }
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-slate-950">
                        {record.stage}
                      </p>

                      <p className="mt-1 text-sm text-slate-700">
                        {record.source}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-800">
                        {record.mode}
                      </span>

                      <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-bold uppercase text-slate-800">
                        {record.status}
                      </span>
                    </div>
                  </div>

                  {record.reason && (
                    <p className="mt-3 text-sm text-slate-700">
                      {record.reason}
                    </p>
                  )}

                  <div className="mt-3 text-xs text-slate-500">
                    <p>
                      Observed: {record.observedAt}
                    </p>

                    {record.callSessionId && (
                      <p className="break-all">
                        Session: {record.callSessionId}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
            No live Calls runtime evidence has been received yet.
            Exercise a real call path to populate this panel.
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-800">
              Controlled simulator
            </p>

            <h4 className="mt-2 text-2xl font-bold text-slate-950">
              Calls Lifecycle Simulator
            </h4>

            <p className="mt-2 max-w-3xl text-sm text-slate-700">
              Runs deterministic lifecycle scenarios in memory.
              It does not create a call session, contact another
              device, send a notification, modify live evidence,
              or certify production behavior.
            </p>
          </div>

          <div className="rounded-xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-700">
            <p>
              Mode: <b>SIMULATED</b>
            </p>

            <p>
              Production writes:{' '}
              <b>
                {workspace.simulatorPolicy
                  .productionWritesAllowed
                  ? 'Allowed'
                  : 'Blocked'}
              </b>
            </p>

            <p>
              Namespace:{' '}
              <b className="break-all">
                {
                  workspace.simulatorPolicy
                    .evidenceNamespace
                }
              </b>
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_auto_auto]">
          <label>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Scenario
            </span>

            <select
              value={
                selectedScenario
              }
              onChange={event =>
                setSelectedScenario(
                  event.target
                    .value as CallsSimulatorScenarioId,
                )
              }
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900"
            >
              {scenarios.map(
                scenario => (
                  <option
                    key={scenario.id}
                    value={scenario.id}
                  >
                    {scenario.label}
                    {scenario.expectedFailure
                      ? ' — failure injection'
                      : ''}
                  </option>
                ),
              )}
            </select>
          </label>

          <button
            type="button"
            onClick={() =>
              setResult(
                runCallsControlledSimulation(
                  selectedScenario,
                ),
              )
            }
            className="self-end rounded-xl bg-violet-700 px-5 py-3 font-bold text-white hover:bg-violet-800"
          >
            Run simulation
          </button>

          <button
            type="button"
            disabled={!result}
            onClick={() =>
              setResult(null)
            }
            className="self-end rounded-xl border border-slate-300 bg-white px-5 py-3 font-bold text-slate-700 disabled:opacity-50"
          >
            Reset
          </button>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Central authority
          </p>

          <p className="mt-2 text-sm text-slate-700">
            CallsLifecycleAuthority remains the only active
            call-session mutation owner.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Evidence boundary
          </p>

          <p className="mt-2 text-sm text-slate-700">
            SIMULATED evidence remains isolated from LIVE device
            and runtime evidence.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Certification
          </p>

          <p className="mt-2 text-sm text-slate-700">
            A simulator pass cannot certify notifications, audio,
            video, background delivery, app-closed delivery, or
            return routing.
          </p>
        </div>
      </section>

      {result ? (
        <section className="rounded-2xl border border-violet-200 bg-white p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700">
                Simulation result
              </p>

              <h4 className="mt-2 text-xl font-bold text-slate-950">
                {result.label}
              </h4>

              <p className="mt-1 break-all text-xs text-slate-500">
                {result.id}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-800">
                {result.mode}
              </span>

              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
                {result.outcome.toUpperCase()}
              </span>

              {result.expectedFailure && (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800">
                  FAILURE INJECTION
                </span>
              )}
            </div>
          </div>

          <div className="mt-5 grid gap-3">
            {result.steps.map(
              step => (
                <div
                  key={
                    step.sequence
                  }
                  className={`rounded-xl border p-4 ${resultClass(
                    step.status,
                  )}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-bold">
                      {step.sequence}.{' '}
                      {step.stage}
                    </p>

                    <span className="text-xs font-bold uppercase">
                      {step.actor} ·{' '}
                      {step.status}
                    </span>
                  </div>

                  <p className="mt-2 text-sm">
                    {step.summary}
                  </p>
                </div>
              ),
            )}
          </div>

          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p>
              Evidence records:{' '}
              <b>
                {result.evidence.length}
              </b>
            </p>

            <p>
              Production writes attempted:{' '}
              <b>
                {String(
                  result.productionWritesAttempted,
                )}
              </b>
            </p>

            <p>
              External delivery attempted:{' '}
              <b>
                {String(
                  result.externalDeliveryAttempted,
                )}
              </b>
            </p>
          </div>
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-slate-600">
          No simulated scenario has been run.
        </section>
      )}
    </div>
  );
}
